import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Plus, Trash2, Loader2, ChevronDown, ChevronUp, ImagePlus } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { Budget, BudgetItem } from '../../types';
import { useAppStore } from '../../store';
import ErrorModal from './ErrorModal';
import { useMemo } from 'react';
import { getCurrentArgentinaISOString, adjustDateForInput } from '../../utils/dateUtils';
import { generateBudgetPDF } from '../../utils/pdfGenerator';
import { removeBudgetItemImageFromStorage } from '../../utils/budgetAttachments';
import { supabase } from '../../lib/supabase';

type BudgetItemForm = Omit<BudgetItem, 'budget_id' | 'created_at'> & {
  id?: string;
  pendingImage?: File | null;
  previewUrl?: string | null;
};

const emptyItem = (taxRate = 0): BudgetItemForm => ({
  description: '',
  quantity: 1,
  unit_price: 0,
  tax_rate: taxRate,
  total: 0,
  image_url: null,
  pendingImage: null,
  previewUrl: null,
});

const IVA_OPTIONS = [0, 10.5, 21];
const RECARGO_OPTIONS = [0, 2, 3, 5, 10];
const BUDGET_TYPES = [
  { value: 'A' as const, label: 'A' },
  { value: 'B' as const, label: 'B' },
  { value: 'C' as const, label: 'C' },
  { value: 'X' as const, label: 'X' },
];

interface BudgetModalProps {
  isOpen: boolean;
  onClose: () => void;
  entity_id: string;
  entity_type: 'client' | 'supplier';
  entityName: string;
  budgetToEdit?: Budget;
}

const BudgetModal: React.FC<BudgetModalProps> = ({
  isOpen,
  onClose,
  entity_id,
  entity_type,
  entityName,
  budgetToEdit
}) => {
  const { addBudget, updateBudget } = useAppStore();
  const [items, setItems] = useState<BudgetItemForm[]>([]);
  const [budgetType, setBudgetType] = useState<'A' | 'B' | 'C' | 'X'>('X');
  const [date, setDate] = useState(getCurrentArgentinaISOString());
  const [validUntil, setValidUntil] = useState('');
  const [status] = useState<'draft' | 'sent' | 'approved' | 'rejected' | 'expired'>('draft');
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [hidePdfTotals, setHidePdfTotals] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const loadBudget = async () => {
      setShowAdvanced(false);
      if (budgetToEdit) {
        setBudgetType(budgetToEdit.type);
        setDate(adjustDateForInput(budgetToEdit.date));
        setValidUntil(budgetToEdit.valid_until ? adjustDateForInput(budgetToEdit.valid_until) : '');
        setHidePdfTotals(!!budgetToEdit.hide_pdf_totals);

        let budgetItems = budgetToEdit.items;
        if (!budgetItems?.length) {
          const { data, error } = await supabase
            .from('budget_items')
            .select('*')
            .eq('budget_id', budgetToEdit.id)
            .order('created_at', { ascending: true });
          if (error) console.error('Error loading budget items:', error);
          budgetItems = data || [];
        }

        if (budgetItems.length) {
          setItems(budgetItems.map(item => ({
            id: item.id,
            description: item.description,
            quantity: item.quantity,
            unit_price: item.unit_price,
            tax_rate: item.tax_rate,
            total: item.total,
            image_url: item.image_url ?? null,
            pendingImage: null,
            previewUrl: item.image_url ?? null,
          })));
        } else {
          setItems([emptyItem(budgetToEdit.type === 'X' ? 0 : 21)]);
        }
      } else {
        setBudgetType('X');
        setDate(getCurrentArgentinaISOString());
        setValidUntil('');
        setHidePdfTotals(false);
        setItems([emptyItem()]);
      }
    };

    loadBudget();
  }, [isOpen, budgetToEdit]);

  const defaultTaxRate = budgetType === 'X' ? 0 : 21;
  const addItem = () => {
    setItems([...items, emptyItem(defaultTaxRate)]);
  };

  const removeItem = (i: number) => {
    if (items.length > 1) {
      const removed = items[i];
      if (removed.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(removed.previewUrl);
      setItems(items.filter((_, idx) => idx !== i));
    }
  };

  const setItemImage = (index: number, file: File | null) => {
    setItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      if (item.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(item.previewUrl);
      if (!file) {
        return { ...item, pendingImage: null, previewUrl: item.image_url ?? null };
      }
      return {
        ...item,
        pendingImage: file,
        previewUrl: URL.createObjectURL(file),
      };
    }));
  };

  const clearItemImage = (index: number) => {
    const item = items[index];
    if (item.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(item.previewUrl);
    if (item.image_url) removeBudgetItemImageFromStorage(item.image_url);
    setItems(prev => prev.map((row, i) =>
      i === index ? { ...row, pendingImage: null, previewUrl: null, image_url: null } : row
    ));
  };

  const updateItem = (index: number, field: keyof BudgetItemForm, value: unknown) => {
    setItems(items.map((item, i) => {
      if (i !== index) return item;
      const updated = { ...item, [field]: value };
      if (field === 'quantity' || field === 'unit_price' || field === 'tax_rate') {
        const q = Math.max(0, updated.quantity);
        const p = Math.max(0, updated.unit_price);
        const t = Math.max(0, updated.tax_rate);
        updated.quantity = q;
        updated.unit_price = p;
        updated.tax_rate = t;
        updated.total = q * p * (1 + t / 100);
      }
      return updated;
    }));
  };

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.quantity * i.unit_price, 0);
    const tax = items.reduce((s, i) => s + (i.quantity * i.unit_price * i.tax_rate / 100), 0);
    return { subtotal, tax, total: subtotal + tax };
  }, [items]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (items.length === 0 || items.some(i => !i.description.trim())) {
      setError('Agregá al menos un ítem con descripción');
      setShowErrorModal(true);
      return;
    }
    if (budgetToEdit?.status === 'converted') {
      setError('No se puede editar un presupuesto ya convertido a factura');
      setShowErrorModal(true);
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const budgetData = {
        date,
        entity_id,
        entity_type,
        type: budgetType,
        subtotal: totals.subtotal,
        tax: totals.tax,
        total: totals.total,
        status,
        valid_until: validUntil || null,
        hide_pdf_totals: hidePdfTotals,
      };
      if (budgetToEdit) {
        await updateBudget(budgetToEdit.id, budgetData, items);
      } else {
        const created = await addBudget(budgetData, items);
        await generateBudgetPDF(
          { ...created, hide_pdf_totals: hidePdfTotals, items: created.items },
          entityName
        );
      }
      onClose();
    } catch (err: any) {
      setError(err.message || 'Error al guardar');
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={onClose}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 w-[95vw] max-w-xl -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white dark:bg-gray-900 shadow-xl overflow-hidden focus:outline-none">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {budgetToEdit ? 'Editar presupuesto' : 'Nuevo presupuesto'}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{entityName}</p>
                </div>
                <Dialog.Close asChild>
                  <button className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Cerrar">
                    <X className="h-5 w-5" />
                  </button>
                </Dialog.Close>
              </div>
              <div className="flex flex-wrap gap-3 mt-4">
                <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
                  {BUDGET_TYPES.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setBudgetType(value)}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        budgetType === value ? 'bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
                />
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[65vh]">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ítems</span>
                  <button type="button" onClick={addItem} className="flex items-center gap-1.5 text-sm text-indigo-600 dark:text-indigo-400 hover:underline">
                    <Plus className="h-4 w-4" /> Agregar
                  </button>
                </div>
                {items.map((item, idx) => (
                  <div key={idx} className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 space-y-2">
                    <div className="flex gap-2 items-center flex-wrap">
                    <input
                      type="text"
                      placeholder="Descripción"
                      value={item.description}
                      onChange={(e) => updateItem(idx, 'description', e.target.value)}
                      className="flex-1 min-w-[140px] px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm placeholder-gray-400"
                    />
                    <input
                      type="number"
                      min="0"
                      step="1"
                      placeholder="Cant"
                      value={item.quantity || ''}
                      onChange={(e) => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                      className="w-16 px-2 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-right"
                    />
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Precio"
                      value={item.unit_price || ''}
                      onChange={(e) => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                      className="w-24 px-2 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-right"
                    />
                    <div className="flex flex-col gap-0.5">
                      <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400">
                        {budgetType === 'X' ? 'Recargo' : 'IVA'} %
                      </label>
                      {(() => {
                        const opts = budgetType === 'X' ? RECARGO_OPTIONS : IVA_OPTIONS;
                        const isCustom = !opts.includes(item.tax_rate);
                        return (
                          <>
                            <select
                              value={isCustom ? 'other' : item.tax_rate}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === 'other') {
                                  const val = opts.includes(item.tax_rate) ? 0.01 : item.tax_rate;
                                  updateItem(idx, 'tax_rate', val);
                                  return;
                                }
                                updateItem(idx, 'tax_rate', parseFloat(v));
                              }}
                              className="w-20 px-2 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm"
                            >
                              {opts.map(v => (
                                <option key={v} value={v}>{v}%</option>
                              ))}
                              <option value="other">Otro</option>
                            </select>
                            {isCustom && (
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.1"
                                value={item.tax_rate}
                                onChange={(e) => updateItem(idx, 'tax_rate', parseFloat(e.target.value) || 0)}
                                className="w-20 px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-right mt-0.5"
                              />
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <span className="w-20 text-sm font-medium text-gray-700 dark:text-gray-300 text-right">${item.total.toFixed(0)}</span>
                    <button type="button" onClick={() => removeItem(idx)} className="p-1.5 text-gray-400 hover:text-red-500 rounded" disabled={items.length === 1}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="inline-flex items-center gap-1.5 text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer text-gray-600 dark:text-gray-300">
                        <ImagePlus className="h-3.5 w-3.5" />
                        Imagen
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={e => {
                            const file = e.target.files?.[0] ?? null;
                            e.currentTarget.value = '';
                            if (file) setItemImage(idx, file);
                          }}
                        />
                      </label>
                      {item.previewUrl && (
                        <>
                          <img
                            src={item.previewUrl}
                            alt=""
                            className="h-10 w-10 rounded object-cover border border-gray-200 dark:border-gray-700"
                          />
                          <button
                            type="button"
                            onClick={() => clearItemImage(idx)}
                            className="text-xs text-red-500 hover:underline"
                          >
                            Quitar imagen
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800">
                <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                  <span>Subtotal</span><span>${totals.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400 mt-1">
                  <span>{budgetType === 'X' ? 'Recargo' : 'IVA'}</span><span>${totals.tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-base font-semibold text-gray-900 dark:text-white mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <span>Total</span><span>${totals.total.toFixed(2)}</span>
                </div>
              </div>

              <label className="mt-4 flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hidePdfTotals}
                  onChange={e => setHidePdfTotals(e.target.checked)}
                  className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  No mostrar totales en el PDF
                  <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    El PDF listará solo los artículos, sin subtotal ni total
                  </span>
                </span>
              </label>

              <div className="mt-4">
                <button type="button" onClick={() => setShowAdvanced(!showAdvanced)} className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700">
                  {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  Más opciones
                </button>
                {showAdvanced && (
                  <div className="mt-3 p-4 rounded-xl border border-gray-200 dark:border-gray-700 space-y-3">
                    <Input type="date" label="Válido hasta" id="validUntil" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
                  </div>
                )}
              </div>

              <div className="mt-6 flex gap-3">
                <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancelar</Button>
                <Button type="submit" disabled={items.filter(i => i.description.trim()).length === 0 || isSubmitting} className="flex-1">
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (budgetToEdit ? 'Actualizar' : 'Crear')}
                </Button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      <ErrorModal isOpen={showErrorModal} onClose={() => setShowErrorModal(false)} message={error || 'Error'} />
    </>
  );
};

export default BudgetModal;
